import { extend } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';
import Post from 'flarum/common/models/Post';
import Button from 'flarum/common/components/Button';
import PostDislikedNotification from './components/PostDislikedNotification';

function isLikedByActor(post) {
  const likes = typeof post.likes === 'function' ? post.likes() : null;
  return !!(app.session.user && likes && likes.some((user) => user === app.session.user));
}

function clearDislikeOptimistically(post) {
  if (!post.attribute('isDisliked')) return;

  post.pushAttributes({
    isDisliked: false,
    dislikesCount: Math.max(0, (post.attribute('dislikesCount') || 0) - 1),
  });
}

function dislikeSummary(post, dislikesCount) {
  const isDisliked = post.attribute('isDisliked');
  const firstDislikedByDisplayName = post.attribute('firstDislikedByDisplayName');

  if (isDisliked) {
    if (dislikesCount === 1) {
      return app.translator.trans('happydigital-post-reactions.forum.post.disliked_by_self');
    }

    return app.translator.trans('happydigital-post-reactions.forum.post.disliked_by_self_and_others', {
      count: dislikesCount - 1,
    });
  }

  if (firstDislikedByDisplayName) {
    if (dislikesCount === 1) {
      return app.translator.trans('happydigital-post-reactions.forum.post.disliked_by_user', {
        username: firstDislikedByDisplayName,
      });
    }

    return app.translator.trans('happydigital-post-reactions.forum.post.disliked_by_user_and_others', {
      username: firstDislikedByDisplayName,
      count: dislikesCount - 1,
    });
  }

  return app.translator.trans('happydigital-post-reactions.forum.post.dislikes_text', { count: dislikesCount });
}

app.initializers.add('happydigital-post-reactions', () => {
  app.notificationComponents.postDisliked = PostDislikedNotification;

  Post.prototype.dislikes = Post.hasMany('dislikes');

  extend(Post.prototype, 'save', function (result, data) {
    // Mutual exclusivity: liking a post (via flarum/likes or otherwise) clears an existing dislike
    if (data && data.isLiked) {
      clearDislikeOptimistically(this);
      m.redraw();
    }
  });

  extend(CommentPost.prototype, 'actionItems', function (items) {
    const post = this.attrs.post;

    if (!app.forum.attribute('happydigitalShowDislikeButton')) return;
    if (post.isHidden() || !app.session.user) return;

    const isDisliked = post.attribute('isDisliked');

    items.add(
      'dislike',
      <Button
        icon="fas fa-thumbs-down"
        className={'Button Button--link HappydigitalPostDislikeButton' + (isDisliked ? ' active' : '')}
        onclick={() => {
          const nextIsDisliked = !post.attribute('isDisliked');

          // Optimistic update
          post.pushAttributes({
            isDisliked: nextIsDisliked,
            dislikesCount: (post.attribute('dislikesCount') || 0) + (nextIsDisliked ? 1 : -1),
          });
          m.redraw();

          // Mutual exclusivity: disliking removes an existing like
          if (nextIsDisliked && isLikedByActor(post)) {
            const likes = post.likes();
            if (likes) {
              const index = likes.indexOf(app.session.user);
              if (index !== -1) {
                likes.splice(index, 1);
              }
            }
            post.save({ isLiked: false });
          }

          app
            .request({
              method: 'POST',
              url: app.forum.attribute('apiUrl') + '/happydigital/posts/' + post.id() + '/dislike',
            })
            .then((response) => {
              post.pushAttributes({
                isDisliked: response.isDisliked,
                dislikesCount: response.dislikesCount,
                firstDislikedByDisplayName: response.firstDislikedByDisplayName,
              });
              m.redraw();
            });
        }}
      >
      </Button>,
      0
    );
  });

  extend(CommentPost.prototype, 'footerItems', function (items) {
    const post = this.attrs.post;

    if (!app.forum.attribute('happydigitalShowDislikeCount')) return;

    const dislikesCount = post.attribute('dislikesCount') || 0;

    if (dislikesCount > 0) {
      items.add(
        'happydigital-dislikes',
        <div className="Post-likedBy HappydigitalPostDislikes">
          <i className="far fa-thumbs-down Post-likedBy-icon HappydigitalPostDislikes-icon" aria-hidden="true"></i>
          {dislikeSummary(post, dislikesCount)}
        </div>,
        85
      );
    }
  });
});
